import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowRight, Check, Send, Package, Users, Truck, Search, Wine, Building2, Boxes, MapPin } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

// Use Case Card
function UseCaseCard({ icon: Icon, title, text }) {
  return (
    <Link 
      to="/buyer-match"
      className="group rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:border-copper/40 hover:shadow-lg"
      data-testid={`use-case-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-12 h-12 rounded-xl bg-copper/10 flex items-center justify-center mb-4 group-hover:bg-copper/20 transition-colors">
        <Icon className="w-6 h-6 text-copper" />
      </div>
      <h3 className="font-heading font-semibold text-lg text-navy group-hover:text-copper transition-colors">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
      <div className="mt-4 text-copper text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        Get Matched <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

// Step Card
function StepCard({ number, title, text }) {
  return (
    <div className="relative">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-copper text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
          {number}
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-navy">{title}</h3>
          <p className="mt-2 text-slate-600 text-sm leading-relaxed">{text}</p>
        </div>
      </div>
    </div>
  );
}

// Benefit Item
function BenefitItem({ children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-5 h-5 rounded-full bg-copper/10 flex items-center justify-center flex-shrink-0">
        <Check className="w-3 h-3 text-copper" />
      </div>
      <span className="text-slate-700">{children}</span>
    </div>
  );
}

export default function Home() {
  useSEO('home');

  const useCases = [
    {
      icon: Wine,
      title: "Find a Producer",
      text: "Source tequila, spirits, wine, water, or functional beverage partners based on your category and market."
    },
    {
      icon: Package,
      title: "Find a Co-Packer",
      text: "Match with facilities for RTDs, canned cocktails, bottled water, private label, and scale-up production."
    },
    {
      icon: Boxes,
      title: "Source Packaging",
      text: "Identify bottle, can, label, and closure partners aligned with your format and target price."
    },
    {
      icon: Truck,
      title: "Find Distribution",
      text: "Connect with distributor-friendly partners and route-to-market support where relationships matter."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Tell us what you need",
      text: "Submit your category, volume, timeline, packaging, and target market in one clean intake form."
    },
    {
      number: "2",
      title: "We review for fit",
      text: "BevRelay screens inquiries for commercial relevance so suppliers see better, more actionable opportunities."
    },
    {
      number: "3",
      title: "Get matched fast",
      text: "Relevant partners move into direct conversations for pricing, capabilities, production, or distribution."
    }
  ];

  return (
    <Layout>
      {/* HERO - Above the Fold */}
      <section className="bg-navy" data-testid="hero-section">
        <div className="container-custom py-16 md:py-24">
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 rounded-full bg-copper/20 text-copper text-sm font-semibold tracking-wide mb-6">
              Beverage sourcing made easier
            </span>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Find the right beverage supplier — without the guesswork.
            </h1>
            
            <p className="mt-6 text-xl text-white/80 leading-relaxed max-w-3xl">
              Get matched with qualified producers, co-packers, packaging partners, and distributors based on your exact needs.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/buyer-match"
                className="btn-copper text-lg px-8 py-4"
                data-testid="hero-get-matched-btn"
              >
                <Send className="w-5 h-5 mr-2" />
                Get Matched
              </Link>
              <span className="text-white/60 text-sm self-center">
                Takes 60 seconds. No commitment.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-slate-100 border-b border-slate-200" data-testid="trust-bar">
        <div className="container-custom py-6">
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-600">
            <span className="font-medium">Used by:</span>
            <span className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-copper" />
              Beverage founders
            </span>
            <span className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-copper" />
              Distributors
            </span>
            <span className="flex items-center gap-2">
              <Search className="w-4 h-4 text-copper" />
              Retail buyers
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-copper" />
              Hospitality groups
            </span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-padding bg-white" data-testid="how-it-works" id="how-it-works">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              How BevRelay Works
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              A simple process to connect you with the right partners
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-10">
              {steps.map((step) => (
                <StepCard key={step.number} {...step} />
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <Link
                to="/buyer-match"
                className="btn-copper inline-flex items-center gap-2"
                data-testid="how-it-works-cta"
              >
                <Send className="w-4 h-4" />
                Get Matched Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="section-padding bg-slate-50" data-testid="use-cases" id="use-cases">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              What Are You Looking For?
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Tell us your needs and we'll connect you with the right partners
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {useCases.map((useCase) => (
              <UseCaseCard key={useCase.title} {...useCase} />
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/buyer-match"
              className="btn-copper inline-flex items-center gap-2"
              data-testid="use-cases-cta"
            >
              <Send className="w-4 h-4" />
              Get Matched
            </Link>
          </div>
        </div>
      </section>

      {/* WHY BEVRELAY */}
      <section className="section-padding bg-white" data-testid="why-bevrelay">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
                Skip the Cold Outreach.<br />
                Get Straight to Opportunities.
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                BevRelay filters and matches you with relevant partners based on your actual needs — 
                not random directories or unqualified leads.
              </p>
              
              <div className="mt-8 space-y-4">
                <BenefitItem>No endless cold outreach</BenefitItem>
                <BenefitItem>No random supplier directories</BenefitItem>
                <BenefitItem>No wasted time on poor-fit leads</BenefitItem>
                <BenefitItem>Only relevant beverage connections</BenefitItem>
              </div>
              
              <div className="mt-10">
                <Link
                  to="/buyer-match"
                  className="btn-copper inline-flex items-center gap-2"
                  data-testid="why-bevrelay-cta"
                >
                  <Send className="w-4 h-4" />
                  Get Matched
                </Link>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200">
              <h3 className="font-heading font-semibold text-xl text-navy mb-6">
                Built for the Beverage Industry
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center flex-shrink-0">
                    <Wine className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy">Brands launching products</h4>
                    <p className="text-sm text-slate-600 mt-1">Find producers, packaging, and co-pack partners</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center flex-shrink-0">
                    <Truck className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy">Operators scaling distribution</h4>
                    <p className="text-sm text-slate-600 mt-1">Connect with distributors and route-to-market support</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-100">
                  <div className="w-10 h-10 rounded-lg bg-copper/10 flex items-center justify-center flex-shrink-0">
                    <Search className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-navy">Buyers sourcing inventory</h4>
                    <p className="text-sm text-slate-600 mt-1">Discover curated suppliers by category and market</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CTA */}
      <section className="bg-navy py-16 md:py-24" data-testid="main-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
            Tell Us What You Need — We'll Handle the Rest
          </h2>
          <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto">
            Get matched with qualified beverage partners in 24-48 hours.
          </p>
          <div className="mt-10">
            <Link
              to="/buyer-match"
              className="btn-copper text-lg px-10 py-4 inline-flex items-center gap-2"
              data-testid="main-cta-btn"
            >
              <Send className="w-5 h-5" />
              Get Matched
            </Link>
          </div>
        </div>
      </section>

      {/* FOR SUPPLIERS (Secondary) */}
      <section className="section-padding bg-slate-50" data-testid="suppliers-section" id="suppliers">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-copper font-semibold text-sm uppercase tracking-wider">
              For Suppliers
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-navy">
              Are You a Supplier?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Get access to qualified buyer opportunities with volume, packaging, market, and timing signals upfront.
            </p>
            
            <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200">
                <Check className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">Qualified buyer inquiries with context</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200">
                <Check className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">Volume and timeline signals upfront</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200">
                <Check className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">Cleaner opportunity flow</span>
              </div>
              <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200">
                <Check className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">Optional premium placement</span>
              </div>
            </div>
            
            <div className="mt-10">
              <Link
                to="/suppliers"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-navy text-navy font-semibold rounded-xl hover:bg-navy hover:text-white transition-colors"
                data-testid="supplier-cta-btn"
              >
                Join as Supplier
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COALITION TOOLS (Brief mention) */}
      <section className="bg-white border-t border-slate-200 py-12" data-testid="tools-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h3 className="font-heading text-xl font-semibold text-navy">
              Coalition Member Tools
            </h3>
            <p className="text-slate-600 text-sm mt-2">
              Additional resources for BevRelay members
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/target-list" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">
              <MapPin className="w-4 h-4 inline mr-1" />
              Target List
            </Link>
            <Link to="/trade-pay" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">
              <Building2 className="w-4 h-4 inline mr-1" />
              Trade Pay Board
            </Link>
            <Link to="/ai-blueprint" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">
              AI Blueprint
            </Link>
            <Link to="/build-brand" className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors">
              Build Brand
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
